import { buildMenuSectionsFromFeatures } from "./menuConfig";

import {
  canAny,
  getFeatureNavigationPermission,
  type FeatureMetadata,
  type NavigationPersona,
} from "@/features/feature.types";
import { FEATURES_BY_MODULE, getFeatureById } from "@/features/featureRegistry";

export type CanPermissionFn = (permission?: string) => boolean;

export type BestAccessibleRouteOptions = {
  /** Feature IDs to try first (in order), e.g. ["dashboard"] */
  preferredFeatureIds?: string[];
  /** Used when no accessible feature exists */
  fallbackPath?: string;
  /** Navigation mode affects which route should feel like the user's home. */
  persona?: NavigationPersona;
};

function isLiveFeature(
  feature: FeatureMetadata | undefined | null,
): feature is FeatureMetadata {
  return Boolean(feature && feature.readiness === "live");
}

function canNavigateToFeature(
  feature: FeatureMetadata,
  can: CanPermissionFn,
  persona: NavigationPersona,
): boolean {
  if (!feature.navigation?.path) return false;
  return canAny(can, getFeatureNavigationPermission(feature, persona));
}

function getFeaturesByModuleForSidebar(
  persona: NavigationPersona,
): Record<string, readonly FeatureMetadata[]> {
  // Mirror Sidebar behavior:
  // - Dashboard is pinned separately (if permitted)
  // - The home module should not include dashboard inside the module groups
  const next: Record<string, readonly FeatureMetadata[]> = {};

  for (const [moduleKey, features] of Object.entries(FEATURES_BY_MODULE)) {
    const filtered =
      moduleKey === "home" && persona !== "guardian"
        ? features.filter((f) => f.id !== "dashboard")
        : features;
    if (filtered.length > 0) {
      next[moduleKey] = filtered;
    }
  }

  return next;
}

/**
 * Computes the best (highest-priority) accessible route for the current user.
 * Priority rules:
 * 1) Try preferred feature IDs first (default: dashboard)
 * 2) Otherwise pick the first accessible live feature by module order, then navigation.order, then label
 */
export function getBestAccessiblePath(
  can: CanPermissionFn,
  options?: BestAccessibleRouteOptions,
): string {
  const persona = options?.persona ?? "staff";
  const preferredFeatureIds = options?.preferredFeatureIds ?? ["dashboard"];
  const fallbackPath = options?.fallbackPath ?? "/403";

  for (const featureId of preferredFeatureIds) {
    const feature = getFeatureById(featureId);
    if (isLiveFeature(feature) && canNavigateToFeature(feature, can, persona)) {
      return feature.navigation.path;
    }
  }

  // Follow the sidebar sequence:
  // - module groups ordered by menu builder
  // - within group, items ordered by navigation.order (then label)
  const sidebarSections = buildMenuSectionsFromFeatures(
    getFeaturesByModuleForSidebar(persona),
    can,
    { persona },
  );
  const firstHref = sidebarSections[0]?.groups[0]?.items[0]?.href;
  return firstHref ?? fallbackPath;
}
